// src/services/authService.js
import apiClient from "../apiClient";
import { loginRequest } from '../msalConfig';
import SecureStorageService from './secureStorageService';
import { resetMsalInitialization } from './msalInstanceManager';

const CACHE_KEYS = {
  AUTH_ERROR_COUNT: 'authErrorCount',
  LAST_ERROR_TIME: 'lastErrorTime',
};

// Use SecureStorageService for all sensitive data
// Keep a simple wrapper for backwards compatibility with existing code
const secureStorage = {
  set: (key, value) => SecureStorageService.set(key, value, 60),
  get: (key) => SecureStorageService.get(key),
  remove: (key) => SecureStorageService.remove(key),
  clear: () => SecureStorageService.clear()
};

// Add rate limiting for auth attempts
const checkRateLimit = () => {
  const errorCount = parseInt(secureStorage.get(CACHE_KEYS.AUTH_ERROR_COUNT) || '0');
  const lastErrorTime = parseInt(secureStorage.get(CACHE_KEYS.LAST_ERROR_TIME) || '0');
  const now = Date.now();

  if (errorCount > 5 && (now - lastErrorTime) < 300000) { // 5 minutes lockout
    throw new Error('Too many login attempts. Please try again later.');
  }
};

// Enhanced login handler
export const handleLogin = async (instance) => {
  try {
    await checkRateLimit();
    await instance.loginRedirect(loginRequest);
    secureStorage.remove(CACHE_KEYS.AUTH_ERROR_COUNT);
  } catch (error) {
    const errorCount = parseInt(secureStorage.get(CACHE_KEYS.AUTH_ERROR_COUNT) || '0');
    secureStorage.set(CACHE_KEYS.AUTH_ERROR_COUNT, errorCount + 1);
    secureStorage.set(CACHE_KEYS.LAST_ERROR_TIME, Date.now());
    throw new Error(error.message || 'Login failed');
  }
};

// Enhanced logout handler - completely clears all authentication state
export const handleLogout = async (instance) => {
  try {
    console.log('Starting comprehensive logout process...');
    
    // Get the current active account before clearing
    const account = instance.getActiveAccount();
    
    // Step 1: Clear all our custom storage
    secureStorage.clear();
    SecureStorageService.clearAuthenticationData();
    
    // Step 2: Clear all MSAL accounts from the instance
    const accounts = instance.getAllAccounts();
    if (accounts && accounts.length > 0) {
      console.log(`Removing ${accounts.length} account(s) from MSAL cache`);
    }
    
    // Step 3: Clear active account
    instance.setActiveAccount(null);
    
    // Step 4: Reset MSAL initialization state
    resetMsalInitialization();
    
    // Step 5: Clear service worker caches if available
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
        console.log('Service worker caches cleared');
      } catch (cacheError) {
        console.warn('Failed to clear service worker caches:', cacheError);
      }
    }
    
    // Step 6: Perform B2C logout redirect (clears B2C session)
    if (account) {
      console.log('Logging out account from Azure B2C:', account.username);
      await instance.logoutRedirect({
        account: account,
        postLogoutRedirectUri: window.location.origin + '/',
      });
      // This will redirect - code below won't execute
    } else {
      console.log('No account found, clearing state and redirecting to landing page');
      window.location.href = '/';
    }
  } catch (error) {
    console.error('Logout error:', error);
    
    // Ensure complete cleanup even if something fails
    try {
      secureStorage.clear();
      SecureStorageService.clearAuthenticationData();
      resetMsalInitialization();
      instance.setActiveAccount(null);
      
      // Force clear all MSAL-related items from localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('msal.')) {
          localStorage.removeItem(key);
        }
      });
      
      console.log('Emergency cleanup completed');
    } catch (cleanupError) {
      console.error('Error during emergency cleanup:', cleanupError);
    }
    
    // Force redirect to landing page
    window.location.href = '/';
  }
};

// Fixed user creation function to handle duplicate creation attempts with proper locking
export const createUserAfterLogin = async (instance, account) => {
  const email = account.username || account.idTokenClaims?.emails?.[0];
  
  if (!email) {
    console.error("Cannot create user: No email found in account");
    return null;
  }

  // Check if user is already initialized or initialization is in progress
  if (SecureStorageService.isUserInitialized()) {
    console.log('User already initialized, skipping creation');
    return null;
  }

  // Check if initialization is already in progress (prevents race condition)
  if (SecureStorageService.isUserInitializationInProgress()) {
    console.log('User initialization already in progress, waiting...');
    // Wait for initialization to complete (max 5 seconds)
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (SecureStorageService.isUserInitialized()) {
          clearInterval(checkInterval);
          resolve(null);
        }
      }, 500);
      
      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        resolve(null);
      }, 5000);
    });
  }

  // Mark initialization as in progress
  SecureStorageService.startUserInitialization();

  try {
    const client = await apiClient(instance);
    
    // First try to get the user to see if they exist
    try {
      const response = await client.get(`/api/User/by-email?email=${encodeURIComponent(email)}`);
      // User exists, no need to create
      console.log('User already exists in database');
      SecureStorageService.completeUserInitialization();
      return response.data;
    } catch (getError) {
      // Only proceed if user definitely doesn't exist (404)
      if (!getError.response || getError.response.status !== 404) {
        console.error('Error checking user existence:', getError);
        SecureStorageService.resetUserInitialization();
        throw getError;
      }
      
      console.log('User not found, creating new user');
      // Match payload to what UserController.CreateUser expects
      const payload = {
        email: email,
        azureAdB2CUserId: account.localAccountId || account.idTokenClaims.sub
      };
      
      console.log('Creating user with payload:', payload);
      const createResponse = await client.post('/api/User', payload);
      
      SecureStorageService.completeUserInitialization();
      return createResponse.data;
    }
  } catch (error) {
    console.error("Error creating/retrieving user:", error);
    SecureStorageService.resetUserInitialization();
    // Don't throw the error - let the flow continue even if user creation failed
    // as the user might already exist due to a race condition
    return null;
  }
};

// Enhanced role fetching with retry mechanism
export const fetchUserRoles = async (instance, account, retryCount = 3) => {
  for (let i = 0; i < retryCount; i++) {
    try {
      const client = await apiClient(instance);
      const email = account.username || (account.idTokenClaims?.emails && account.idTokenClaims.emails[0]);
      if (!email) {
        throw new Error('No email found in account');
      }
      
      const response = await client.get(`/api/User/roles?email=${encodeURIComponent(email)}`);
      const roles = response.data.$values;
      
      // Store roles in SecureStorageService with 2-hour expiration
      SecureStorageService.setRoles(roles);
      
      return roles;
    } catch (error) {
      if (i === retryCount - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1))); // Exponential backoff
    }
  }
  return [];
};

// Add session monitoring
export const initializeSessionMonitor = (instance) => {
  let sessionCheckInterval;

  const checkSession = async () => {
    try {
      const account = instance.getActiveAccount();
      if (!account) return;

      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });

      if (!response) {
        handleLogout(instance);
      }
    } catch (error) {
      console.error('Session check failed:', error);
      if (error.name === 'InteractionRequiredAuthError') {
        handleLogout(instance);
      }
    }
  };

  // Check session every 5 minutes
  sessionCheckInterval = setInterval(checkSession, 300000);

  // Cleanup on unmount
  return () => clearInterval(sessionCheckInterval);
};

// Function to handle profile edit
export const handleEditProfile = async (instance) => {
  const editProfileRequest = {
    authority: `https://${process.env.REACT_APP_B2C_TENANT}.b2clogin.com/${process.env.REACT_APP_B2C_TENANT}.onmicrosoft.com/${process.env.REACT_APP_B2C_EDIT_PROFILE_POLICY}`,
    scopes: ["openid", "profile"],
  };
  try {
    await instance.loginRedirect(editProfileRequest);
  } catch (error) {
    console.error("Edit profile error:", error);
    throw error;
  }
};
